import React, { useContext } from "react";
import { CourseContext } from "contexts/CourseContext";
import { DateTime } from "luxon";
import { isNullOrUndefined } from "utils/general";

/**
 * Get the current time in PDT.
 * @param now - The time to convert to PDT, defaults to the current time.
 * @returns The current time in PDT, formatted as YYYY-MM-DDTHH:MM.
 */
export function getCurrPTTime(now = new Date()): string {
    // make sure time is in PT
    const offset = now.getTimezoneOffset();
    now.setHours(now.getHours() - offset / 60 - 7);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    console.log(`${year}-${month}-${day}T${hours}:${minutes}`)
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }


/**
 * Format a date as a full month and day with the appropriate suffix.
 * e.g. "January 1st", "June 9th", etc.
 * @param date - The date to format.
 * @returns The formatted date string.
 */
export function formateDateWithSuffix(date: Date): string {
  if(date == null) {
    return ""
  }
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' });
  const dateStr = formatter.format(date);

  const pluralRules = new Intl.PluralRules('en-US', { type: 'ordinal' });
  const suffix = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
  }[pluralRules.select(date.getDate())];
return dateStr.replace(/\d+/, date.getDate() + suffix);
}



/**
 * Get the current time in a given timezone.
 * @param timezoneString - The timezone to get the time in.
 * @returns The current time in the given timezone, formatted as HH:MM am/pm.
 */
export function getTimeInTimezone(timezoneString: string): string {
  const [sign, hours, minutes] = timezoneString.match(/([-+])(\d+):(\d+)/).slice(1);
  const timezoneOffset = ((Number(hours) * 60) + Number(minutes)) * (sign === '-' ? -1 : 1);
  const currentTime = new Date();
  const utcTime = currentTime.getTime() + (currentTime.getTimezoneOffset() * 60000);
  const timezoneTime = utcTime + (timezoneOffset * 60000);
  const timezoneDate = new Date(timezoneTime);
  const hoursFormatted = timezoneDate.getHours() % 12 || 12;
  const minutesFormatted = timezoneDate.getMinutes().toString().padStart(2, '0');
  const amPm = timezoneDate.getHours() < 12 ? 'am' : 'pm';
  return `${hoursFormatted}:${minutesFormatted} ${amPm}`;
}


/**
 * Get the time until a given date.
 * @param nextDate - The date to get the time until.
 * @param now - The current time, defaults to the current time.
 * @returns The time until the given date.
 */
export function getTimeUntil(nextDate: Date, now=new Date()){
  if(!nextDate) {
    return ""
  }

    const timeUntil = Math.abs(nextDate.getTime() - now.getTime());
    let secondsUntil = Math.floor(timeUntil / 1000);
    let minutesUntil = Math.floor(secondsUntil / 60);
    let hoursUntil = Math.floor(minutesUntil / 60);
    let daysUntil = Math.floor(hoursUntil / 24);

    if(nextDate.getTime() < now.getTime()){
      secondsUntil *= -1
      minutesUntil *= -1
      hoursUntil *= -1
      daysUntil *= -1
    }
  
    const rtf = new Intl.RelativeTimeFormat(navigator.language, { style: 'long' });
    const kindDayString = Math.abs(daysUntil) > 1 ? 'days' : 'day';
    const kindHourString = Math.abs(hoursUntil) > 1 ? 'hours' : 'hour';

  
    if (Math.abs(daysUntil) > 0) {
      return `${Math.abs(daysUntil)} ${kindDayString} and ${rtf.format(hoursUntil % 24, 'hour')}`;
    } else if (Math.abs(hoursUntil) > 0) {
      return `${Math.abs(hoursUntil)} ${kindHourString} and ${rtf.format(minutesUntil % 60, 'minute')}`
    } else if (Math.abs(minutesUntil) > 0) {
      return rtf.format(minutesUntil, 'minute');
    } else {
      return rtf.format(secondsUntil, 'second');
    }
  }
  


export const getFormattedString = function (dayName: string, hours: number, mins: number) {
  if (mins === 0) {
    // special case for midnight
    if (hours === 0) {
      return dayName + ", Midnight";
    }
    if (hours === 12) {
      return dayName + ", Noon";
    }
    // special case for noon
    var suffix = "am";
    if (hours > 12) {
      suffix = "pm";
      hours -= 12;
    }
    return dayName + ", " + hours + suffix;
  } else{
    // special case for noon
    let suffix = "am";
    if (hours === 0) {
      return dayName + ", " + `12:${mins}am (just after Midnight)`;
    } else if (hours === 12) {
      return dayName + ", " + `12:${mins}pm (just after Noon)`;
    } else if (hours >= 12) {
      suffix = "pm";
      hours -= 12;
    }
    return dayName + ", " + hours + `:${mins}` + suffix;
  }
};

export const getTimezoneName = function (timezoneString: string) {
  const parts = timezoneString.split(":");
  const hours = parseInt(parts[0][parts[0].length - 1] + parts[1].substring(0, 2));
  const minutes = parseInt(parts[1].substring(2));
  const utcOffset = hours * 60 + minutes;
  const timezoneName = Intl.DateTimeFormat(undefined, { timeZone: `Etc/GMT${parts[0]}` }).resolvedOptions().timeZone;
  const tz = timezoneName.replace(/[^A-Za-z\/_]/g, '');
  const city = tz.split('/')[1];
  return "Stanford"
  return city;
}



const parseUTCOffset = function (tzString: string) {
  var parsedString = tzString.slice(3);
  var multiplier = parsedString[0] === "+" ? 1 : -1;
  parsedString = parsedString.slice(1);
  var [hours, minutes] = parsedString.split(":");

  return {
    hours: multiplier * parseInt(hours),
    minutes: multiplier * parseInt(minutes),
  };
};

const removeOffset = function (date: Date, offset: { hours: number, minutes: number }) {
  date.setHours(date.getHours() - offset.hours);
  date.setMinutes(date.getMinutes() - offset.minutes);
  return date;
};

const addOffset = function (date: Date, offset: { hours: number, minutes: number }) {
  date.setHours(date.getHours() + offset.hours);
  date.setMinutes(date.getMinutes() + offset.minutes);
  return date;
};

const padTo2 = (number: number, pad: boolean, withSign: boolean) => {
  const absValue = "" + Math.abs(number);
  let paddedValue = pad ? absValue.padStart(2, "0") : absValue;
  if (withSign) {
    if (number < 0) {
      paddedValue = "-" + paddedValue;
    } else {
      paddedValue = "+" + paddedValue;
    }
  }
  return paddedValue;
};

// TODO: This function should accept the current server time, not rely on the
// client's local time.
export const getLocalUTCTimezone = function () {
  const offset = new Date().getTimezoneOffset();
  const numHours = ~~(offset / 60); // get floor
  const numMins = padTo2(offset % 60, true, false);
  const diffNum = numHours * -1;
  const paddedNum = padTo2(diffNum, false, true);
  return "UTC" + paddedNum + ":" + numMins;
};

export const shiftToTimezone = function (date: Date, srcTzString: string, dstTzString: string) {
  removeOffset(date, parseUTCOffset(srcTzString));
  addOffset(date, parseUTCOffset(dstTzString));
  return date;
};

export const getLocalTime = function (timeZoneStr: string, dateTime: Date) {
  // all times in this program should be CA time, UTC-7.
  return shiftToTimezone(dateTime, "UTC-7:00", timeZoneStr);
};


export const parseAndFormatISODate = (dateISO: string, targetTz: string) => {
    // Parse the ISO date in UTC (since ISO 8601 with 'Z' indicates UTC)
    const utcDate = DateTime.fromISO(dateISO, { zone: 'utc' });
    
    // Shift to the target timezone
    const targetDate = utcDate.setZone(targetTz);
    
    // Format the date in the target timezone
    const formattedDate = targetDate.toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
    
    return formattedDate;
}


// given a date, return string in the format of "May, 21st"
export const monthDayString = (date) => {
  if (date == null) {
    return ""
  }
  const month = date.toLocaleString('default', { month: 'long' });
  const day = date.getDate();
  const suffix = (day % 10 === 1 && day !== 11) ? 'st' :
                 (day % 10 === 2 && day !== 12) ? 'nd' :
                 (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
  
  return `${month} ${day}${suffix}`;
}